import { Stack, Typography } from '@mui/material';
import { formatQuarterLabel } from '@schooly/components/charts';
import { SimpleDropdown } from '@schooly/style';
import {
  eachQuarterOfInterval,
  endOfQuarter,
  getQuarter,
  getYear,
  startOfQuarter,
  subYears,
} from 'date-fns';
import { FC } from 'react';
import { useIntl } from 'react-intl';

type Quarter = {
  name: string;
  startDate: Date;
  endDate: Date;
};

type Year = {
  name: string;
  value: string;
};

export const getQuartersForYear = (year: number) => {
  const interval = {
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31),
  };

  return eachQuarterOfInterval(interval).map(getQuarterForDate);
};

export const getQuarterForDate = (date: Date): Quarter => {
  return {
    name: formatQuarterLabel(getQuarter(date).toString()),
    startDate: startOfQuarter(date),
    endDate: endOfQuarter(date),
  };
};

export type DashboardFiltersProps = {
  currentQuarter: Quarter;
  onDateChange: (date: [Date, Date], compareDate: [Date, Date]) => void;
  years: Year[];
  previousYearNumber: number;
  currentDate: Date;
};

export const DashboardFilters: FC<DashboardFiltersProps> = ({
  onDateChange,
  years,
  currentQuarter,
  previousYearNumber,
  currentDate,
}) => {
  const currentYearNumber = getYear(currentDate);
  const { formatMessage } = useIntl();
  const currentYear = years.find((year) => year.value === currentYearNumber.toString());
  const quarters = getQuartersForYear(currentYearNumber);

  return (
    <Stack direction="row" alignItems="center">
      <Typography variant="h3" color="common.grey2" pr={1}>
        {formatMessage({ id: 'dashboard-select-period' })}
      </Typography>
      <SimpleDropdown
        defaultValue={currentQuarter}
        currentValue={currentQuarter}
        options={quarters}
        onChange={(quarter) => {
          const previousYearNumber = getYear(subYears(quarter.startDate, 1));
          const previousQuarters = getQuartersForYear(previousYearNumber);
          const previousQuarter = previousQuarters.find(
            (previousQuarter) => quarter.name === previousQuarter.name,
          );

          if (!previousQuarter) return;

          onDateChange(
            [quarter.startDate, quarter.endDate],
            [previousQuarter.startDate, previousQuarter.endDate],
          );
        }}
      />
      <SimpleDropdown
        defaultValue={currentYear}
        options={years}
        currentValue={currentYear}
        onChange={(year) => {
          const quarters = getQuartersForYear(Number(year.value));
          const quarter = quarters.find((quarter) => currentQuarter?.name === quarter?.name);

          if (!quarter) return;

          const previousYearNumber = getYear(subYears(quarter.startDate, 1));
          const previousQuarters = getQuartersForYear(previousYearNumber);
          const previousQuarter = previousQuarters.find(
            (previousQuarter) => quarter.name === previousQuarter.name,
          );

          if (!previousQuarter) return;

          onDateChange(
            [quarter.startDate, quarter.endDate],
            [previousQuarter.startDate, previousQuarter.endDate],
          );
        }}
      />
      <Stack direction="row" alignItems="center" gap={2.25} pl={2}>
        <Typography variant="h3" color="common.grey3">
          {formatMessage({ id: 'dashboard-compared-with' })}
        </Typography>

        <StatisticsCompareLabel label={`${currentQuarter?.name} ${previousYearNumber}`} />
      </Stack>
    </Stack>
  );
};

export const StatisticsCompareLabel: FC<{
  label: string;
}> = ({ label }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        backgroundColor: 'common.main6',
        py: 0.25,
        px: 0.75,
        borderRadius: 0.5,
      }}
    >
      <Typography variant="h3" color="common.grey2">
        {label}
      </Typography>
    </Stack>
  );
};
